'use client'
import { useEffect, useRef, useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import { Separator } from '@/common/ui/separator'
import useQuerySearchUsersForRematch from '@/modules/transaction/api/hooks/useQuerySearchUsersForRematch'
import { useMutationRematchSmsAutoLog } from '@/modules/transaction/api/hooks/useMutationRematchSmsAutoLog'
import { useMutationUpdateSmsAutoLogStatus } from '@/modules/transaction/api/hooks/useMutationUpdateSmsAutoLogStatus'
import useQueryDepositTransaction from '@/modules/transaction/api/hooks/useQueryDepositTransaction'
import { useQuerySearchAllPlayers } from '@/modules/transaction/api/hooks/useQuerySearchAllPlayers'
import useQueryGetGeneralSetting from '@/modules/client-setting/api/hooks/useQueryGetGeneralSetting'
import { useQueryPlayerTurnover } from '@/modules/transaction/api/hooks/useQueryPlayerTurnover'
import {
	SearchUsersForRematchResultItem,
	SmsAutoLogStatus,
} from '@/modules/transaction/types'
import useQueryGetDepositPromotionById from '@/modules/promotion/api/hooks/useQueryGetDepositPromotionById'

interface RematchDialogProps {
	smsLogId: string
	smsAmount: number
	smsAccountNumber: string
	smsMessage: string | null
	gatewayMinDeposit?: number | null
	isOpen: boolean
	onClose: () => void
}

type DepositListItem = {
	id: string
	txId: string | null
	amount: number
	status: string
	promotionId?: string | null
	promotionFixedDepositAmount?: number | null
}

export default function RematchDialog({
	smsLogId,
	smsAmount,
	smsAccountNumber,
	smsMessage,
	gatewayMinDeposit,
	isOpen,
	onClose,
}: RematchDialogProps) {
	const [step, setStep] = useState<1 | 2>(1)
	const [selectedUser, setSelectedUser] =
		useState<SearchUsersForRematchResultItem | null>(null)
	const [depositTransactionId, setDepositTransactionId] = useState('')
	const [newAmount, setNewAmount] = useState<string>('')
	const [note, setNote] = useState('')
	const [searchQuery, setSearchQuery] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchQuery)
		}, 400)
		return () => clearTimeout(timer)
	}, [searchQuery])

	const processingSetRef = useRef<boolean>(false)
	const submittedRef = useRef<boolean>(false)

	const updateStatusMutation = useMutationUpdateSmsAutoLogStatus()

	const { data: searchData, isLoading: isSearching } =
		useQuerySearchUsersForRematch({
			smsLogId: isOpen ? smsLogId : '',
			...(debouncedSearch ? { accountNumber: debouncedSearch } : {}),
		})

	const { data: allPlayerResults, isLoading: isSearchingAll } =
		useQuerySearchAllPlayers(debouncedSearch)

	const allUsers = searchData?.data ?? []
	const filteredUsers = allUsers

	useEffect(() => {
		if (isOpen && smsLogId && !processingSetRef.current) {
			processingSetRef.current = true
			updateStatusMutation.mutate({
				id: smsLogId,
				status: SmsAutoLogStatus.PROCESSING,
			})
		}
		if (!isOpen) {
			processingSetRef.current = false
			submittedRef.current = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	const { data: depositData, isLoading: isLoadingDeposits } =
		useQueryDepositTransaction(
			{
				playerId: selectedUser?.playerId,
				status: ['CREATED', 'PENDING'] as never,
				take: 20,
				skip: 0,
			} as never,
			{
				queryKey: [
					'deposits-for-rematch',
					selectedUser?.playerId,
				] as unknown as readonly unknown[],
				enabled: step === 2 && !!selectedUser?.playerId,
			} as never,
		)

	const depositItems = ((
		depositData as unknown as { data?: DepositListItem[] } | undefined
	)?.data ?? []) as DepositListItem[]

	const { data: turnoverStatus } = useQueryPlayerTurnover(
		step === 2 ? selectedUser?.playerId : null,
	)

	const pendingDeposits = depositItems.filter(
		(d) => d.status === 'CREATED' || d.status === 'PENDING',
	)

	const selectedDeposit = pendingDeposits.find(
		(d) => d.id === depositTransactionId,
	)

	const promotionId = selectedDeposit?.promotionId
	const { data: promotionDetail } = useQueryGetDepositPromotionById(
		promotionId || '',
		{ enabled: !!promotionId } as never,
	)

	const { mutate, isPending } = useMutationRematchSmsAutoLog()

	const { data: generalSetting } = useQueryGetGeneralSetting()
	const globalMinDeposit = generalSetting?.minimumDepositAmount ?? 0

	const promotionMinDeposit =
		promotionDetail?.fixedDepositAmount ??
		promotionDetail?.claimConstraint
			?.map((c) => ('amount' in c ? (c as { amount: number }).amount : 0))
			.filter((v) => v > 0)
			.reduce((max, v) => Math.max(max, v), 0) ??
		0

	const mins = [
		globalMinDeposit,
		gatewayMinDeposit,
		promotionMinDeposit,
	].filter((v): v is number => v != null && v > 0)
	const effectiveMinDeposit = mins.length > 0 ? Math.max(...mins) : 0

	const handleBack = () => {
		setStep(1)
		setSelectedUser(null)
		setDepositTransactionId('')
		setNewAmount('')
		setNote('')
		setSearchQuery('')
		setDebouncedSearch('')
	}

	const handleGoToStep2 = () => {
		if (!selectedUser) return
		setStep(2)
	}

	const resetAndClose = () => {
		setStep(1)
		setSelectedUser(null)
		setDepositTransactionId('')
		setNewAmount('')
		setNote('')
		setSearchQuery('')
		setDebouncedSearch('')
		onClose()
	}

	const handleClose = () => {
		if (!submittedRef.current && smsLogId) {
			updateStatusMutation.mutate(
				{
					id: smsLogId,
					status: SmsAutoLogStatus.UNMATCHED,
				},
				{
					onSettled: () => resetAndClose(),
				},
			)
		} else {
			resetAndClose()
		}
	}

	const parsedNewAmount = newAmount ? parseFloat(newAmount) : undefined
	const amountChanged =
		parsedNewAmount !== undefined && parsedNewAmount !== smsAmount
	const noteRequired = amountChanged && !note.trim()

	const handleSubmit = () => {
		if (noteRequired) return
		const finalAmount = parsedNewAmount ?? smsAmount
		if (effectiveMinDeposit > 0 && finalAmount < effectiveMinDeposit) {
			const ok = window.confirm(
				`⚠ ยอดเงินต่ำกว่าขั้นต่ำ ${effectiveMinDeposit.toLocaleString()} บาท\n\nคุณต้องการดำเนินการต่อหรือไม่?`,
			)
			if (!ok) return
		}
		mutate(
			{
				id: smsLogId,
				data: {
					depositTransactionId:
						selectedDeposit?.txId || depositTransactionId || undefined,
					playerId: selectedUser!.playerId,
					newAmount: parsedNewAmount ?? smsAmount,
					note: note.trim() || undefined,
				},
			} as never,
			{
				onSuccess: () => {
					submittedRef.current = true
					handleClose()
				},
			},
		)
	}

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{step === 1 ? 'เติมเครดิตให้ลูกค้า' : 'ระบุรายการฝากเงิน'}
					</DialogTitle>
				</DialogHeader>

				{step === 1 && (
					<div className="space-y-5 py-2">
						{/* Description */}
						<div className="space-y-1 text-center text-sm text-muted-foreground">
							<p>
								เติมเครดิตให้ลูกค้าจากรายการโอน (ยอดเงิน{' '}
								{smsAmount.toLocaleString()} บาท)
							</p>
							{(smsMessage || smsAccountNumber) && (
								<p className="text-xs">
									รายละเอียด: {smsMessage || smsAccountNumber}
								</p>
							)}
						</div>

						{/* Search input */}
						<Input
							placeholder="ค้นหาเบอร์โทร, ชื่อบัญชี, เลขบัญชี..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>

						{/* ค้นหาทั่วทั้งระบบ */}
						{debouncedSearch.length >= 3 && (
							<div className="space-y-2">
								<p className="text-sm font-medium">🔍 ค้นหาทั่วทั้งระบบ</p>
								{isSearchingAll && (
									<p className="py-2 text-sm text-muted-foreground">
										กำลังค้นหา...
									</p>
								)}
								{!isSearchingAll && allPlayerResults?.length === 0 && (
									<p className="py-2 text-sm text-muted-foreground">
										ไม่พบผู้ใช้
									</p>
								)}
								{!isSearchingAll &&
									allPlayerResults &&
									allPlayerResults.length > 0 && (
										<div className="max-h-48 space-y-2 overflow-y-auto">
											{allPlayerResults.map((player) => (
												<div
													key={player.id}
													className="flex items-center gap-3 rounded-lg border p-3"
												>
													<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/20 text-sm font-semibold text-primary">
														{player.phone?.charAt(0) ?? '?'}
													</div>
													<div className="min-w-0 flex-1 space-y-0.5">
														<p className="text-sm font-medium">
															{player.phone}
														</p>
														{player.username && (
															<p className="text-xs text-muted-foreground">
																@{player.username}
															</p>
														)}
														{player.accountNumber && (
															<p className="text-xs text-muted-foreground">
																{player.accountName} ({player.accountNumber}) ·{' '}
																{player.bankProvider}
															</p>
														)}
													</div>
													<Button
														type="button"
														size="sm"
														variant={
															selectedUser?.playerId === player.id
																? 'default'
																: 'outline'
														}
														onClick={() => {
															if (selectedUser?.playerId === player.id) {
																setSelectedUser(null)
															} else {
																setSelectedUser({
																	playerId: player.id,
																	maskedPhone: player.phone,
																	accountNumber: player.accountNumber ?? '-',
																	accountName:
																		player.accountName ??
																		player.username ??
																		'-',
																	bankProvider: player.bankProvider ?? '-',
																	pendingDepositCount: 0,
																})
																setStep(2)
															}
														}}
													>
														{selectedUser?.playerId === player.id
															? 'เลือกแล้ว'
															: 'เลือก'}
													</Button>
												</div>
											))}
										</div>
									)}
							</div>
						)}

						{/* ลูกค้าที่คาดว่า */}
						<div className="space-y-2">
							<p className="text-sm font-medium">💡 ลูกค้าที่คาดว่า</p>

							{isSearching && !debouncedSearch && (
								<p className="py-2 text-sm text-muted-foreground">
									กำลังค้นหา...
								</p>
							)}

							{!isSearching && filteredUsers.length === 0 && (
								<p className="py-2 text-sm text-muted-foreground">
									ไม่พบลูกค้า
								</p>
							)}

							{!isSearching && filteredUsers.length > 0 && (
								<div className="max-h-64 space-y-2 overflow-y-auto">
									{filteredUsers.map((user) => (
										<div
											key={user.playerId}
											className="flex items-center gap-3 rounded-lg border p-3"
										>
											<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/20 text-sm font-semibold text-primary">
												{user.accountName?.charAt(0) ?? '?'}
											</div>
											<div className="min-w-0 flex-1 space-y-0.5">
												<p className="text-sm font-medium">
													{user.maskedPhone}
													{user.accountName && (
														<span className="ml-1 text-muted-foreground">
															({user.accountName})
														</span>
													)}
												</p>
												<p className="text-xs text-muted-foreground">
													เลขบัญชี: {user.accountNumber}
												</p>
												<p className="text-xs text-muted-foreground">
													{user.bankProvider}
													{user.pendingDepositCount > 0 && (
														<span className="ml-1 text-green-500">
															· {user.pendingDepositCount} รายการรอ
														</span>
													)}
												</p>
											</div>
											<Button
												type="button"
												size="sm"
												variant={
													selectedUser?.playerId === user.playerId
														? 'default'
														: 'outline'
												}
												onClick={() => {
													if (selectedUser?.playerId === user.playerId) {
														setSelectedUser(null)
													} else {
														setSelectedUser(user)
														setStep(2)
													}
												}}
											>
												{selectedUser?.playerId === user.playerId
													? 'เลือกแล้ว'
													: 'เลือก'}
											</Button>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				)}

				{step === 2 && selectedUser && (
					<div className="space-y-4 py-2">
						<div className="rounded-md bg-muted p-3 text-sm">
							<p className="font-medium">{selectedUser.maskedPhone}</p>
							<p className="text-muted-foreground">
								{selectedUser.accountNumber} · {selectedUser.bankProvider}
							</p>
						</div>

						{!promotionDetail && effectiveMinDeposit > 0 && (
							<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
								<p className="font-medium text-amber-800">
									⚠ ขั้นต่ำการฝาก ฿{effectiveMinDeposit.toLocaleString()} บาท
								</p>
								<p className="text-xs text-amber-600">
									ยอดเงินใหม่ต้องไม่ต่ำกว่าขั้นต่ำที่ตั้งไว้
								</p>
							</div>
						)}

						{turnoverStatus?.hasActiveTurnover && (
							<div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
								<p className="font-medium text-red-800">
									🚫 ผู้เล่นยังทำยอดเทิร์นไม่ครบ
								</p>
								<p className="text-xs text-red-600">
									ยอดเทิร์นคงเหลือ ฿
									{turnoverStatus.turnoverAmount.toLocaleString()} บาท
									{' · '}
									คงเหลือในกระเป๋า ฿{turnoverStatus.balance.toLocaleString()}{' '}
									บาท
								</p>
							</div>
						)}

						{promotionDetail && (
							<div className="space-y-1.5 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
								<p className="font-medium text-blue-800">
									📋 โปรโมชั่น: {promotionDetail.name}
								</p>
								{effectiveMinDeposit > 0 && (
									<p className="text-sm font-semibold text-blue-800">
										⚠ ขั้นต่ำที่ต้องฝาก: ฿
										{effectiveMinDeposit.toLocaleString()} บาท
									</p>
								)}
								{promotionDetail.turnoverRound != null &&
									promotionDetail.turnoverRound > 0 && (
										<p className="text-xs text-blue-700">
											🔄 ทำเทิร์น:{' '}
											{promotionDetail.turnoverRound.toLocaleString()} เท่า
										</p>
									)}
								{promotionDetail.minimumWithdrawCredit != null &&
									promotionDetail.minimumWithdrawCredit > 0 && (
										<p className="text-xs text-blue-700">
											📤 ขั้นต่ำการถอน: ฿
											{promotionDetail.minimumWithdrawCredit.toLocaleString()}{' '}
											บาท
										</p>
									)}
								{promotionDetail.maximumWithdrawCredit != null &&
									promotionDetail.maximumWithdrawCredit > 0 && (
										<p className="text-xs text-blue-700">
											📥 ขั้นสูงการถอน: ฿
											{promotionDetail.maximumWithdrawCredit.toLocaleString()}{' '}
											บาท
										</p>
									)}
								{promotionDetail.gameTypes?.length > 0 && (
									<p className="text-xs text-blue-700">
										🎮 ประเภทเกม: {promotionDetail.gameTypes.join(', ')}
									</p>
								)}
								<p className="pt-1 text-xs text-blue-600">
									⚠ โปรโมชั่นนี้มีเงื่อนไข — กรุณาตรวจสอบก่อนเติมเงิน
								</p>
							</div>
						)}

						<Separator />

						<div className="space-y-2">
							<Label>รายการฝากเงินที่รออยู่</Label>

							{isLoadingDeposits && (
								<p className="text-sm text-muted-foreground">
									กำลังโหลดรายการฝากเงิน...
								</p>
							)}

							{!isLoadingDeposits && pendingDeposits.length === 0 && (
								<p className="text-sm text-muted-foreground">
									ไม่พบรายการฝากเงินที่รอ
								</p>
							)}

							{!isLoadingDeposits && pendingDeposits.length > 0 && (
								<div className="max-h-48 space-y-2 overflow-y-auto rounded-md border p-2">
									{pendingDeposits.map((deposit) => {
										const isSelected = deposit.id === depositTransactionId
										return (
											<div
												key={deposit.id}
												className={`flex items-center justify-between rounded p-2 ${
													isSelected ? 'bg-primary/10' : 'hover:bg-muted'
												}`}
											>
												<div className="space-y-0.5">
													<p className="text-sm font-medium">
														฿{deposit.amount.toLocaleString()}
													</p>
													<p className="text-xs text-muted-foreground">
														{deposit.txId
															? `TX: ${deposit.txId.slice(0, 12)}... · `
															: ''}
														{deposit.status}
													</p>
												</div>
												<Button
													type="button"
													size="sm"
													variant={isSelected ? 'default' : 'outline'}
													onClick={() =>
														setDepositTransactionId(
															isSelected ? '' : deposit.id,
														)
													}
												>
													{isSelected ? 'เลือกแล้ว' : 'เลือก'}
												</Button>
											</div>
										)
									})}
								</div>
							)}
						</div>

						{depositTransactionId && (
							<>
								<Separator />
								{selectedDeposit && (
									<div className="space-y-1">
										<p className="text-sm text-muted-foreground">
											ยอด deposit ปัจจุบัน: ฿
											{selectedDeposit.amount.toLocaleString()} · ยอด SMS: ฿
											{smsAmount.toLocaleString()}
										</p>
										<p className="text-xs text-muted-foreground">
											{selectedDeposit.txId
												? `TX: ${selectedDeposit.txId.slice(0, 16)}... · `
												: ''}
											สถานะ: {selectedDeposit.status}
										</p>
										{selectedDeposit.amount !== smsAmount && (
											<p className="text-xs text-amber-600">
												⚠ ยอดเงินไม่ตรงกับ SMS — กรุณายืนยันยอดใหม่
											</p>
										)}
									</div>
								)}

								<div className="space-y-2">
									<Label htmlFor="new-amount">
										ยอดเงินใหม่ (ถ้าต่างจากยอด SMS: ฿
										{smsAmount.toLocaleString()})
									</Label>
									<Input
										id="new-amount"
										type="number"
										placeholder={`ยอด SMS: ${smsAmount}`}
										value={newAmount}
										onChange={(e) => setNewAmount(e.target.value)}
									/>
									{effectiveMinDeposit > 0 && (
										<p className="text-xs text-muted-foreground">
											ขั้นต่ำ ฿{effectiveMinDeposit.toLocaleString()} บาท
										</p>
									)}
								</div>

								{amountChanged && (
									<div className="space-y-2">
										<Label htmlFor="rematch-note">
											เหตุผลที่เปลี่ยนยอด{' '}
											<span className="text-destructive">*</span>
										</Label>
										<Textarea
											id="rematch-note"
											placeholder="ระบุเหตุผล..."
											value={note}
											onChange={(e) => setNote(e.target.value)}
											rows={2}
										/>
									</div>
								)}
							</>
						)}
					</div>
				)}

				<DialogFooter>
					{step === 2 && (
						<Button variant="outline" onClick={handleBack} disabled={isPending}>
							ย้อนกลับ
						</Button>
					)}
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={isPending || updateStatusMutation.isPending}
					>
						ปิด
					</Button>
					{step === 1 && (
						<Button onClick={handleGoToStep2} disabled={!selectedUser}>
							Save
						</Button>
					)}
					{step === 2 && (
						<Button
							onClick={handleSubmit}
							disabled={
								noteRequired || isPending || turnoverStatus?.hasActiveTurnover
							}
						>
							{turnoverStatus?.hasActiveTurnover
								? 'ติดเทิร์น - ไม่สามารถทำรายการได้'
								: isPending
									? 'กำลังดำเนินการ...'
									: 'ยืนยันเติมเงิน'}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
